const STARTI_SDK_URL = 'https://cdn.starti.ai/js/starti-pixel-v1.0.0.min.js';
const STARTI_SCRIPT_ID = 'starti-pixel-sdk';

type StartiEventPayload = Record<string, unknown>;
type StartiCommand =
  | ['init', StartiEventPayload]
  | ['identify', StartiEventPayload]
  | ['track', string, StartiEventPayload?]
  | ['pageview', StartiEventPayload?]
  | ['debug', boolean]
  | ['state']
  | ['version'];

type RouterLike = {
  subscribe: (callback: () => void) => () => void;
};

let startiUserId: string | undefined;

declare global {
  interface Window {
    starti?: (...args: StartiCommand) => unknown;
    startiQueue?: StartiCommand[];
    __startiPixelLoading?: boolean;
    __startiPixelInitialized?: boolean;
  }
}

const getStartiConfig = () => ({
  adv_id: process.env.REACT_APP_STARTI_ADV_ID,
  pixel_id: process.env.REACT_APP_STARTI_PIXEL_ID,
  debug: process.env.REACT_APP_STARTI_DEBUG === 'true',
});

const ensureQueue = () => {
  window.startiQueue = window.startiQueue || [];
  return window.startiQueue;
};

const callStarti = (command: StartiCommand) => {
  if (window.starti) {
    window.starti(...command);
    return;
  }

  ensureQueue().push(command);
};

const loadStartiSdk = () => {
  if (window.__startiPixelLoading || document.getElementById(STARTI_SCRIPT_ID)) {
    return;
  }

  window.__startiPixelLoading = true;

  const script = document.createElement('script');
  script.id = STARTI_SCRIPT_ID;
  script.async = true;
  script.src = STARTI_SDK_URL;
  document.head.appendChild(script);
};

const initStartiPixel = () => {
  if (window.__startiPixelInitialized) {
    return true;
  }

  const { adv_id, pixel_id, debug } = getStartiConfig();

  if (!adv_id || !pixel_id) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[Starti] Missing REACT_APP_STARTI_ADV_ID or REACT_APP_STARTI_PIXEL_ID; pageview tracking is disabled.'
      );
    }
    return false;
  }

  ensureQueue();
  callStarti(['init', { adv_id, pixel_id, debug }]);
  loadStartiSdk();
  window.__startiPixelInitialized = true;

  return true;
};

const getStartiUserId = () => {
  return startiUserId;
};

export const setStartiUserId = (userId?: string | number | null) => {
  startiUserId = userId === null || userId === undefined || userId === '' ? undefined : String(userId);
};

export const clearStartiUserId = () => {
  startiUserId = undefined;
};

export const setupStartiPageTracking = (router: RouterLike) => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  let lastTrackedUrl = '';
  let referrerUrl = document.referrer || undefined;

  const trackCurrentPage = () => {
    if (!initStartiPixel()) {
      return;
    }

    const pageUrl = window.location.href;

    if (pageUrl === lastTrackedUrl) {
      return;
    }

    callStarti([
      'pageview',
      {
        page_url: pageUrl,
        referrer_url: referrerUrl,
        ...(getStartiUserId() ? { uid: getStartiUserId() } : {}),
      },
    ]);

    referrerUrl = pageUrl;
    lastTrackedUrl = pageUrl;
  };

  trackCurrentPage();

  return router.subscribe(() => {
    window.setTimeout(trackCurrentPage, 0);
  });
};

export {};
